import React, { useEffect, useState } from 'react';
import {
  Visibility as VisibilityIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import {
  Box,
  ClassNameMap,
  IconButton,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axios, { AxiosError } from 'axios';
import {
  getFilesPerPageByStudentId,
  getFileSignedUrl,
} from '../../../../../requests/api/file';
import { FILES_UI } from '../../../../File/FileListView/types';
import Header from '../../../../../components/Layout/Header';
import Page from '../../../../../components/Layout/Page';
import CustomTable from '../../../../../components/General/CustomTable';
import handleApiResponse from '../../../../../utils/handleApiResponse';
import EmptyTable from '../../../../../components/General/EmptyTable';
import CircularProgressComponent from '../../../../../components/Loading/CircularProgressComponent';
import useStyles from './styles';
import { useAcademicYear } from '../../contexts/AcademicYearContext';

const buttonActions = (
  classes: ClassNameMap<string>,
  seeFunction: () => void,
) => (<Box>
  <IconButton onClick={seeFunction}>
    <VisibilityIcon className={classes.icons} />
  </IconButton>
</Box>);

function FilesListView({ studentId }: { studentId: string }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedYear } = useAcademicYear();
  const year = selectedYear ?? new Date().getFullYear();

  const [allFiles, setAllFiles] = useState({
    items: [],
    totalItems: 0,
  });
  const [loading, setLoading] = useState(true);
  const [filesFilters, setFilesFilters] = useState({
    page: 0,
    rowsPerPage: 10,
    searchValue: '',
  });

  const handleSeeFile = async (fileId: string) => {
    try {
      const { url } = await getFileSignedUrl(fileId);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  useEffect(() => {
    const source = axios.CancelToken.source();
    const getAllFiles = async () => {
      if (!studentId) return;

      try {
        const { rows, count } = await getFilesPerPageByStudentId({
          page: filesFilters.page,
          limit: filesFilters.rowsPerPage,
          searchValue: filesFilters.searchValue,
          studentId,
          year,
        }, source.token);

        const filesWithActions = rows.map((file: {
          id: string,
          fileUrl?: string,
          program?: { programName?: string },
        }) => ({
          ...file,
          programName: file?.program?.programName,
          actions: buttonActions(
            classes,
            () => handleSeeFile(file.id),
          ),
        }));

        setAllFiles({ items: filesWithActions, totalItems: count });
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      } finally {
        setLoading(false);
      }
    };

    getAllFiles();
    return () => {
      source.cancel();
    };
  }, [
    filesFilters.page,
    filesFilters.rowsPerPage,
    filesFilters.searchValue,
    year,
  ]);

  const handleChangeSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilesFilters((prevState) => ({ ...prevState, searchValue: e.target.value }));
    if (filesFilters.page !== 0) {
      setFilesFilters((prevState) => ({ ...prevState, page: 0 }));
    }
  };

  return (
    <Page title="Archivos">
      <Header
        title='Archivos'
        icon={<SchoolIcon fontSize="large" />}
        search={!loading
          ? { text: 'Buscar un archivo...', value: filesFilters.searchValue || '', onChangeSearchValue: handleChangeSearchValue }
          : undefined}
      />
      {loading ? <CircularProgressComponent /> : <Box mt={3}>
        {allFiles.totalItems !== 0
          && <CustomTable
            headers={FILES_UI.filter((elem: { showOnTable: boolean }) => elem.showOnTable)}
            data={{ values: allFiles.items, count: allFiles.totalItems }}
            reduxTableParameters={filesFilters}
            setReduxTableParameters={setFilesFilters}
          />}
        {allFiles.totalItems === 0 && <EmptyTable />}
      </Box>}
    </Page>
  );
}

export default FilesListView;
